import { useCallback, useEffect, useState } from 'react';
import { Search, Users, Loader2, Book } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { CoverImage } from '@/components/CoverImage';
import { DinoAvatar } from '@/components/DinoAvatar';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { normalizeOpenLibraryId, searchOpenLibrary } from '@/lib/searchUtils';
import type { BookResult, UserResult } from '@/types/search';
import { BookSearchSkeleton } from './BookSearchSkeleton';

export function SearchBanner() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'books' | 'people'>('books');
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState<'title' | 'author'>('title');
  const [loading, setLoading] = useState(false);
  const [bookResults, setBookResults] = useState<BookResult[]>([]);
  const [externalResults, setExternalResults] = useState<BookResult[]>([]);
  const [userResults, setUserResults] = useState<UserResult[]>([]);
  const [showExternal, setShowExternal] = useState(false);
  const [loadingExternal, setLoadingExternal] = useState(false);

  // BOOK SEARCH (local only)
  const searchBooks = useCallback(async (q: string) => {
    if (!q.trim()) {
      setBookResults([]);
      setExternalResults([]);
      setShowExternal(false);
      return;
    }
    setLoading(true);
    setShowExternal(false);
    try {
      const { data, error } = await supabase.functions.invoke('search-books', {
        body: { query: q, type: searchType, page: 1, limit: 5, source: 'local' },
      });
      if (error) throw error;
      setBookResults(data?.results || []);
    } catch (e) {
      console.error('Search error', e);
      setBookResults([]);
    } finally {
      setLoading(false);
    }
  }, [searchType]);

  // EXTERNAL SEARCH
  const searchExternal = useCallback(async (q: string) => {
    if (!q.trim()) return;
    setLoadingExternal(true);
    try {
      const { data, error } = await supabase.functions.invoke('search-books', {
        body: { query: q, type: searchType, page: 1, limit: 5, source: 'external' },
      });
      if (error) throw error;
      setExternalResults(data?.results || []);
      setShowExternal(true);
    } catch (e) {
      console.error('External search error', e);
      setExternalResults([]);
    } finally {
      setLoadingExternal(false);
    }
  }, [searchType]);

  // USER SEARCH
  const searchPeople = useCallback(async (q: string) => {
    if (!q.trim()) {
      setUserResults([]);
      return;
    }
    setLoading(true);
    try {
      const { data: users } = await supabase
        .rpc('search_public_profiles', { search_term: q })
        .limit(5);

      setUserResults(users || []);
    } catch (err) {
      console.error('User search error', err);
      setUserResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce
  useEffect(() => {
    if (!open) return;
    if (!query.trim()) {
      setBookResults([]);
      setExternalResults([]);
      setUserResults([]);
      setShowExternal(false);
      return;
    }
    const t = setTimeout(() => {
      activeTab === 'books' ? searchBooks(query) : searchPeople(query);
    }, 300);
    return () => clearTimeout(t);
  }, [query, activeTab, open, searchBooks, searchPeople]);

  const handleBookClick = async (book: BookResult) => {
    setOpen(false);
    setQuery('');
    try {
      if (book.isLocal && book.id) {
        navigate(`/book/${book.id}`);
        return;
      }
      const normalizedId = normalizeOpenLibraryId(book.open_library_id || '');
      if (!normalizedId) throw new Error('Invalid book identifier');

      const { data: existingBooks } = await supabase
        .from('books')
        .select('id, published_year, isbn13, isbn10, open_library_id')
        .or(`open_library_id.eq.${normalizedId},open_library_id.eq./works/${normalizedId}${book.isbn13 ? `,isbn13.eq.${book.isbn13}` : ''}${book.isbn10 ? `,isbn10.eq.${book.isbn10}` : ''}`)
        .limit(5);

      const matchingBook = existingBooks?.find(existing => {
        const yearMatch = !book.published_year || !existing.published_year || existing.published_year === book.published_year;
        const isbnMatch = (book.isbn13 && book.isbn13 === existing.isbn13) || (book.isbn10 && book.isbn10 === existing.isbn10);
        const olidMatch = existing.open_library_id === normalizedId || existing.open_library_id === `/works/${normalizedId}`;
        return (olidMatch && yearMatch) || isbnMatch;
      });

      if (matchingBook) {
        navigate(`/book/${matchingBook.id}`);
        return;
      }

      const { data: newBook, error } = await supabase
        .from('books')
        .insert({
          open_library_id: normalizedId,
          title: book.title || 'Untitled',
          subtitle: book.subtitle,
          authors: book.authors || [],
          published_year: book.published_year,
          cover_url: book.cover_url,
          isbn10: book.isbn10,
          isbn13: book.isbn13,
          synopsis: book.synopsis,
          source: 'openlibrary',
          language: 'en',
          enrichment_status: 'pending',
        })
        .select('id')
        .single();

      if (error) throw error;
      navigate(`/book/${newBook?.id}`);
    } catch (err) {
      console.error('Error opening book', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'There was an error opening the book details.',
      });
    }
  };

  return (
    <Card className="card-elevated bg-gradient-to-r from-primary/5 to-secondary/5">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center w-full">
          {/* Search trigger that opens inline dropdown */}
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={activeTab === 'books' ? (searchType === 'title' ? 'Search by book title…' : 'Search by author name…') : 'Search people…'}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onClick={() => setOpen(true)}
                  className="pl-10"
                />
              </div>
            </PopoverTrigger>
            <PopoverContent 
              className="w-[28rem] p-0 bg-background z-50" 
              align="start" 
              sideOffset={8}
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <div className="p-3 space-y-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    navigate('/search');
                    setOpen(false);
                    setQuery('');
                  }}
                >
                  Go to full search page →
                </Button>
                <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'books' | 'people')}
                  className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="books" className="flex items-center gap-2">
                      <Search className="w-4 h-4" /> Books
                    </TabsTrigger>
                    <TabsTrigger value="people" className="flex items-center gap-2">
                      <Users className="w-4 h-4" /> People
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="books" className="space-y-2">
                    <div className="flex justify-center mb-2">
                      <div className="inline-flex rounded-lg border p-1">
                        <button
                          onClick={() => setSearchType('title')}
                          className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                            searchType === 'title' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          Title
                        </button>
                        <button
                          onClick={() => setSearchType('author')}
                          className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                            searchType === 'author' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          Author
                        </button>
                      </div>
                    </div>

                    {loading ? (
                      <div className="space-y-2">
                        {Array.from({ length: 3 }).map((_, i) => (
                          <div key={i} className="flex items-center gap-3 p-2 animate-pulse">
                            <div className="w-8 h-12 bg-muted rounded" />
                            <div className="flex-1 space-y-1">
                              <div className="h-3 bg-muted rounded w-3/4" />
                              <div className="h-2 bg-muted/60 rounded w-1/2" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : bookResults.length > 0 ? (
                      <>
                        <div className="px-3 py-2 text-xs text-muted-foreground flex items-center gap-1">
                          <Book className="h-3 w-3" />
                          <span>In Library</span>
                        </div>
                        <div className="max-h-64 overflow-y-auto space-y-1">
                          {bookResults.map((book, index) => (
                            <Button
                              key={book.id || book.open_library_id || index}
                              variant="ghost"
                              className="w-full justify-start p-2 h-auto relative"
                              onClick={() => handleBookClick(book)}
                            >
                              {book.isLocal && (
                                <Badge variant="secondary" className="absolute top-1 right-1 text-[9px] px-1 py-0 h-3">
                                  📚
                                </Badge>
                              )}
                              <div className="flex items-center gap-3 w-full">
                                <CoverImage
                                  bookId={book.id}
                                  isbn13={book.isbn13}
                                  isbn10={book.isbn10}
                                  olid={book.open_library_id}
                                  coverUrl={book.cover_url}
                                  title={book.title}
                                  author={book.authors?.[0]}
                                  size="xs"
                                  className="w-8 h-12 flex-shrink-0"
                                />
                                <div className="flex-1 text-left">
                                  <div className="font-medium text-sm truncate">{book.title || 'Untitled'}</div>
                                  <div className="text-xs text-muted-foreground truncate">
                                    {book.authors?.length ? book.authors.join(', ') : 'Unknown author'}
                                  </div>
                                </div>
                              </div>
                            </Button>
                          ))}
                        </div>
                        
                        {!showExternal && bookResults.length > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full mt-2 gap-2"
                            onClick={() => searchExternal(query)}
                            disabled={loadingExternal}
                          >
                            {loadingExternal ? (
                              <>
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Searching...
                              </>
                            ) : (
                              <>
                                🌐 Search web
                              </>
                            )}
                          </Button>
                        )}

                        {externalResults.length > 0 && showExternal && (
                          <>
                            <div className="px-3 py-2 text-xs text-muted-foreground flex items-center gap-1 border-t mt-2">
                              <Search className="h-3 w-3" />
                              <span>From Web</span>
                            </div>
                            <div className="max-h-48 overflow-y-auto space-y-1">
                              {externalResults.map((book) => (
                                <Button
                                  key={book.open_library_id}
                                  variant="ghost"
                                  className="w-full justify-start p-2 h-auto relative"
                                  onClick={() => handleBookClick(book)}
                                >
                                  <Badge variant="outline" className="absolute top-1 right-1 text-[9px] px-1 py-0 h-3">
                                    🔍
                                  </Badge>
                                  <div className="flex items-center gap-3 w-full">
                                    <CoverImage
                                      isbn13={book.isbn13}
                                      isbn10={book.isbn10}
                                      olid={book.open_library_id}
                                      coverUrl={book.cover_url}
                                      title={book.title}
                                      author={book.authors?.[0]}
                                      size="xs"
                                      className="w-8 h-12 flex-shrink-0"
                                    />
                                    <div className="flex-1 text-left">
                                      <div className="font-medium text-sm truncate">{book.title || 'Untitled'}</div>
                                      <div className="text-xs text-muted-foreground truncate">
                                        {book.authors?.length ? book.authors.join(', ') : 'Unknown author'}
                                      </div>
                                    </div>
                                  </div>
                                </Button>
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    ) : query && !loading ? (
                      <div className="text-center py-4 text-sm text-muted-foreground">
                        No books found for "{query}"
                      </div>
                    ) : null}

                    {query && bookResults.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          navigate(`/search?q=${encodeURIComponent(query)}&tab=books`);
                          setOpen(false);
                          setQuery('');
                        }}
                      >
                        View all book results
                      </Button>
                    )}
                  </TabsContent>

                  <TabsContent value="people" className="space-y-2">
                    {loading ? (
                      <div className="space-y-2">
                        {Array.from({ length: 3 }).map((_, i) => (
                          <div key={i} className="flex items-center gap-3 p-2 animate-pulse">
                            <div className="w-8 h-8 bg-muted rounded-full" />
                            <div className="flex-1 space-y-1">
                              <div className="h-3 bg-muted rounded w-1/2" />
                              <div className="h-2 bg-muted/60 rounded w-1/3" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : userResults.length > 0 ? (
                      <div className="max-h-64 overflow-y-auto space-y-1">
                        {userResults.map((person) => (
                          <Button
                            key={person.id}
                            variant="ghost"
                            className="w-full justify-start p-2 h-auto"
                            onClick={() => {
                              navigate(`/u/${person.username}`);
                              setOpen(false);
                              setQuery('');
                            }}
                          >
                            <div className="flex items-center gap-3 w-full">
                              <DinoAvatar src={person.image} name={person.name || person.username} size="xs" />
                              <div className="flex-1 text-left">
                                <div className="font-medium text-sm truncate">{person.name || person.username}</div>
                                {person.username && person.name && (
                                  <div className="text-xs text-muted-foreground truncate">@{person.username}</div>
                                )}
                              </div>
                            </div>
                          </Button>
                        ))}
                      </div>
                    ) : query && !loading ? (
                      <div className="text-center py-4 text-sm text-muted-foreground">
                        No people found for "{query}"
                      </div>
                    ) : null}

                    {query && userResults.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          navigate(`/search?q=${encodeURIComponent(query)}&tab=people`);
                          setOpen(false);
                          setQuery('');
                        }}
                      >
                        View all people results
                      </Button>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            </PopoverContent>
          </Popover>

          {/* Optional quick People search opener */}
          <Button
            variant="pillOutline"
            onClick={() => {
              setActiveTab('people');
              setOpen(true);
              // focus the input after opening
              setTimeout(() => {
                const el = document.querySelector<HTMLInputElement>('input[placeholder^="Search"]');
                el?.focus();
              }, 0);
            }}
            className="w-full sm:w-auto border-[3px] font-semibold shadow-glow"
          >
            <Users className="w-5 h-5 mr-2" />
            Find Friends
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

